import { createSelector } from 'reselect';
import { CreativeDocumentElement } from 'content-creation-common/models';

import { FEATURE_FLAG_NAMES } from '../../../feature-flags/constants';
import { getFeatureFlagValueAsNumber } from '../../../feature-flags/state-management/selectors';
import { isUserAnAdvertiser, isUserAPublisher } from '../../../../selectors/ui';
import {
  DEFAULT_EPISODE_LENGTH_MAX_SECONDS,
  DEFAULT_EPISODE_LENGTH_MAX_SECONDS_FOR_ADVERTISER,
  EPISODE_LENGTH_ABSOLUTE_MAX_SECONDS,
  EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS,
  SNAP_LENGTH_ABSOLUTE_MAX_SECONDS,
  SNAP_LENGTH_MAX_SECONDS,
  SNAP_LENGTH_MIN_SECONDS,
  TIME_RANGE_MIN_FRAME,
} from './constants';
import { clamp, isNotNumber, parseIntElseNull } from '../../../../util/numbers';
import {
  MINUTE_IN_SECONDS,
  NON_STATIC_MEDIA_TYPES,
  PLAYBACK_FRAMES_PER_SECOND,
} from '../../../../constants';
import { isEmpty as isEmptyArray } from '../../../../util/array';
import { getAllElements } from '../../../../selectors/element';
import { getMediaInCreative } from '../../../../selectors/media';
import {
  getDiscoverPublisher,
  getIsPublisherHappeningNow,
} from '../../../discover/state-management/selectors';
import { isNotNil } from '../../../../util/object';

const getMinSnapDurationFromFeatureFlag = (state) => {
  return getFeatureFlagValueAsNumber(
    state,
    FEATURE_FLAG_NAMES.MIN_SNAP_DURATION_IN_SECONDS,
  );
};

export const getMinSnapDurationInSeconds = createSelector(
  [getMinSnapDurationFromFeatureFlag],
  (featureFlagMin) => {
    if (isNotNumber(featureFlagMin)) return SNAP_LENGTH_MIN_SECONDS;
    return Math.max(featureFlagMin, 0);
  },
);

const getMaxSnapDurationFromFeatureFlag = (state) => {
  return getFeatureFlagValueAsNumber(
    state,
    FEATURE_FLAG_NAMES.MAX_SNAP_DURATION_IN_SECONDS,
  );
};

export const getMaxSnapDurationInSeconds = createSelector(
  [
    getMaxSnapDurationFromFeatureFlag,
    getMinSnapDurationInSeconds,
    getDiscoverPublisher,
    isUserAPublisher,
  ],
  (featureFlagMax, minSnapDuration, discoverPublisher, isUserAPublisher) => {
    let maxSnapDuration = featureFlagMax;
    if (
      isUserAPublisher &&
      isNotNil(discoverPublisher) &&
      isNotNil(discoverPublisher.topSnapLimit)
    ) {
      // If the discoverPublisher has a max, prefer that over the feature flag
      const discoverSnapDuration = parseIntElseNull(
        discoverPublisher.topSnapLimit,
      );
      if (isNotNil(discoverSnapDuration)) {
        maxSnapDuration = discoverSnapDuration;
      }
    }
    if (isNotNumber(maxSnapDuration)) return SNAP_LENGTH_MAX_SECONDS;
    return clamp(
      maxSnapDuration,
      minSnapDuration,
      SNAP_LENGTH_ABSOLUTE_MAX_SECONDS,
    );
  },
);

const getDefaultMaxEpisodeDuration = createSelector(
  [isUserAnAdvertiser],
  (isAdvertiser) => {
    return isAdvertiser
      ? DEFAULT_EPISODE_LENGTH_MAX_SECONDS_FOR_ADVERTISER
      : DEFAULT_EPISODE_LENGTH_MAX_SECONDS;
  },
);

const getMaxEpisodeDurationFromFeatureFlag = (state) => {
  return getFeatureFlagValueAsNumber(
    state,
    FEATURE_FLAG_NAMES.MAX_EPISODE_DURATION_IN_MINUTES,
  );
};

export const getMaxEpisodeDurationInSeconds = createSelector(
  [
    getMaxEpisodeDurationFromFeatureFlag,
    getMinSnapDurationInSeconds,
    getDefaultMaxEpisodeDuration,
  ],
  (featureFlagMaxInMinutes, minSnapDuration, defaultSnapDuration) => {
    if (isNotNumber(featureFlagMaxInMinutes)) {
      return defaultSnapDuration + EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS;
    }
    const featureFlagMaxInSeconds =
      featureFlagMaxInMinutes * MINUTE_IN_SECONDS +
      EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS;
    return clamp(
      featureFlagMaxInSeconds,
      minSnapDuration,
      EPISODE_LENGTH_ABSOLUTE_MAX_SECONDS,
    );
  },
);

export const getTimeRangeMaxFrame = createSelector(
  [getMaxSnapDurationInSeconds],
  (maxSnapDuration) => {
    return Math.floor(maxSnapDuration * PLAYBACK_FRAMES_PER_SECOND) - 1;
  },
);

export const getTimeRangeMinFrame = (state) => {
  return TIME_RANGE_MIN_FRAME;
};

export const getHasNonStaticContents = createSelector(
  [getMediaInCreative, getAllElements],
  (mediaInCreative, allElements) => {
    if (isEmptyArray(mediaInCreative)) return false;

    const hasNonStaticMedia = mediaInCreative.some((media) => {
      return NON_STATIC_MEDIA_TYPES.has(media.mediaType);
    });

    if (hasNonStaticMedia) return true;
    if (isEmptyArray(allElements)) return false;
    return allElements.some((element) => {
      const layer = CreativeDocumentElement.fromJSON(element);
      return isNotNil(layer.transforms) && layer.transforms.size > 1;
    });
  },
);

export const getIsImageSnap = createSelector(
  [getIsPublisherHappeningNow, getHasNonStaticContents],
  (isPublisherHappeningNow, hasNonStaticContents) => {
    // Only applies to HN Publishers
    // Return true if there isn't any motion/audible content
    return isPublisherHappeningNow && !hasNonStaticContents;
  },
);

export const getMaxVideoPublicationDurationFromFeatureFlag = (state) => {
  return getFeatureFlagValueAsNumber(
    state,
    FEATURE_FLAG_NAMES.MAX_VIDEO_PUBLICATION_DURATION_IN_MINUTES,
  );
};

export const getMaxVideoPublicationDurationInSeconds = createSelector(
  [
    getMaxVideoPublicationDurationFromFeatureFlag,
    getMinSnapDurationInSeconds,
    getDefaultMaxEpisodeDuration,
    getDiscoverPublisher,
    getIsPublisherHappeningNow,
  ],
  (
    featureFlagMaxVideoPublicationInMinutes,
    minSnapDuration,
    defaultSnapDuration,
    discoverPublisher,
    isPublisherHappeningNow,
  ) => {
    if (
      isUserAPublisher &&
      isNotNil(discoverPublisher) &&
      isPublisherHappeningNow &&
      isNotNil(discoverPublisher.topSnapLimit)
    ) {
      // Happening Now only allows 1 Snap per Story so Snap limit here is effectively the max duration
      const discoverSnapDuration = parseIntElseNull(
        discoverPublisher.topSnapLimit,
      );
      if (isNotNil(discoverSnapDuration)) {
        return discoverSnapDuration;
      }
    }

    if (isNotNumber(featureFlagMaxVideoPublicationInMinutes)) {
      return defaultSnapDuration + EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS;
    }
    const featureFlagMaxVideoPublicationInSeconds =
      featureFlagMaxVideoPublicationInMinutes * MINUTE_IN_SECONDS +
      EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS;
    return clamp(
      featureFlagMaxVideoPublicationInSeconds,
      minSnapDuration,
      EPISODE_LENGTH_ABSOLUTE_MAX_SECONDS,
    );
  },
);

export const getMaxVideoPublicationDurationInMinutes = createSelector(
  [getMaxVideoPublicationDurationInSeconds],
  (maxVideoPublicationDurationInSeconds) =>
    maxVideoPublicationDurationInSeconds / MINUTE_IN_SECONDS,
);
